import { normalizeInstructorType } from "../constants/instructors";
import {
   filterReservationsAllPages,
   getAllReservations,
   getInstructorReservations,
} from "../api/reservationsService";

export function isSubstituteInstructorType(value) {
   return normalizeInstructorType(value) === "SCHIMB";
}

export function getReservationBaseInstructorId(row) {
   return (
      row?.instructorId ??
      row?.instructor_id ??
      row?.instructor?.id ??
      row?.reservation?.instructorId ??
      row?.reservation?.instructor_id ??
      row?.reservation?.instructor?.id ??
      null
   );
}

export function hasActiveReservationSubstitution(row) {
   const substitution =
      row?.substitution ??
      row?.reservation?.substitution ??
      row?.instructorSubstitution ??
      row?.reservation?.instructorSubstitution ??
      null;
   if (!substitution) return false;
   if (substitution?.isActive === false || substitution?.is_active === false) {
      return false;
   }
   return true;
}

function getReservationSubstitutionBaseInstructorId(row) {
   const substitution =
      row?.substitution ??
      row?.reservation?.substitution ??
      row?.instructorSubstitution ??
      row?.reservation?.instructorSubstitution ??
      null;
   if (!substitution) return null;
   if (substitution?.isActive === false || substitution?.is_active === false) {
      return null;
   }
   const baseId =
      substitution?.baseInstructorId ??
      substitution?.base_instructor_id ??
      substitution?.baseInstructor?.id ??
      substitution?.base_instructor?.id ??
      null;
   if (baseId == null || baseId === "") return null;
   return baseId;
}

export function getReservationColumnInstructorId(row) {
   const substitutionBaseId = getReservationSubstitutionBaseInstructorId(row);
   if (substitutionBaseId != null && substitutionBaseId !== "") {
      return substitutionBaseId;
   }
   return getReservationBaseInstructorId(row);
}

export function getReservationEffectiveInstructorId(row) {
   const effectiveId =
      row?.effectiveInstructor?.id ??
      row?.effective_instructor?.id ??
      row?.reservation?.effectiveInstructor?.id ??
      row?.reservation?.effective_instructor?.id ??
      null;
   if (effectiveId !== null && effectiveId !== undefined && effectiveId !== "") {
      return effectiveId;
   }

   if (hasActiveReservationSubstitution(row)) {
      const substitution =
         row?.substitution ??
         row?.reservation?.substitution ??
         row?.instructorSubstitution ??
         row?.reservation?.instructorSubstitution ??
         null;
      const substituteId =
         substitution?.substituteInstructorId ??
         substitution?.substitute_instructor_id ??
         null;
      if (substituteId !== null && substituteId !== undefined && substituteId !== "") {
         return substituteId;
      }
   }

   return getReservationBaseInstructorId(row);
}

export function reservationBelongsToInstructor(row, instructor) {
   const instructorId = String(instructor?.id ?? "").trim();
   if (!instructorId || !row) return false;

   if (isSubstituteInstructorType(instructor?.instructorType)) {
      return (
         String(getReservationEffectiveInstructorId(row) ?? "").trim() ===
         instructorId
      );
   }

   return (
      String(getReservationBaseInstructorId(row) ?? "").trim() === instructorId
   );
}

const DEFAULT_PAGE_OPTS = {
   pageSize: 500,
   maxItems: 5000,
   preferSinglePage: true,
   singlePageLimit: 5000,
   singlePageAssumeCompleteIfUnderLimit: true,
};

export async function fetchInstructorReservationsForRange(
   instructor,
   user,
   { startDateFrom = "", startDateTo = "" } = {},
   pageOpts = DEFAULT_PAGE_OPTS,
) {
   if (!instructor?.id) return [];

   const sort = { sortBy: "startTime", sortOrder: "asc" };
   const range = { startDateFrom, startDateTo };

   if (isSubstituteInstructorType(instructor.instructorType)) {
      try {
         const byEffective = await filterReservationsAllPages(
            {
               effectiveInstructorId: Number(instructor.id),
               ...range,
               ...sort,
            },
            pageOpts,
         );
         return byEffective.filter((row) =>
            reservationBelongsToInstructor(row, instructor),
         );
      } catch {}

      try {
         const byDate = await filterReservationsAllPages(
            {
               ...range,
               ...sort,
            },
            pageOpts,
         );
         return byDate.filter((row) =>
            reservationBelongsToInstructor(row, instructor),
         );
      } catch {}

      try {
         const fallback = await getInstructorReservations(instructor.id, user?.id);
         const list = Array.isArray(fallback) ? fallback : [];
         return list.filter((row) =>
            reservationBelongsToInstructor(row, instructor),
         );
      } catch {
         const all = await getAllReservations();
         return (Array.isArray(all) ? all : []).filter((row) =>
            reservationBelongsToInstructor(row, instructor),
         );
      }
   }

   try {
      return await filterReservationsAllPages(
         {
            instructorId: Number(instructor.id),
            ...range,
            ...sort,
         },
         pageOpts,
      );
   } catch {
      try {
         const fallback = await getInstructorReservations(instructor.id, user?.id);
         return Array.isArray(fallback) ? fallback : [];
      } catch {
         const all = await getAllReservations();
         return (Array.isArray(all) ? all : []).filter(
            (row) =>
               String(getReservationBaseInstructorId(row) ?? "") ===
                  String(instructor.id) ||
               String(row?.instructor?.userId ?? "") === String(user?.id ?? ""),
         );
      }
   }
}

export function resolveScopedCalendarInstructorId(
   row,
   { scopedInstructorId = "", substituteScheduleMode = false } = {},
) {
   const scopedId = String(scopedInstructorId || "").trim();
   if (!scopedId) {
      return String(getReservationColumnInstructorId(row) ?? "").trim();
   }

   if (substituteScheduleMode) {
      const effectiveId = String(getReservationEffectiveInstructorId(row) ?? "").trim();
      if (effectiveId !== scopedId) return "";
      return scopedId;
   }

   const columnId = String(getReservationColumnInstructorId(row) ?? "").trim();
   if (columnId !== scopedId) return "";
   return scopedId;
}
